import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Apple, Coffee, Sun, Moon as MoonIcon, Cookie, Check, Loader2, Plus, CalendarPlus, Dumbbell, Flame, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const MEAL_ICONS = {
  reggeli: Coffee,
  ebéd: Sun,
  vacsora: MoonIcon,
  snack: Cookie
};

const MICRO_CHECKLIST = [
  { name: "D-vitamin", status: true },
  { name: "Omega-3", status: true },
  { name: "Magnézium", status: true },
  { name: "Cink", status: false },
  { name: "B12-vitamin", status: true },
  { name: "Vas", status: false },
  { name: "Kalcium", status: true },
  { name: "C-vitamin", status: true },
];

export default function NutritionPage() {
  const { api } = useAuth();
  const [meals, setMeals] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mealForm, setMealForm] = useState({
    meal_type: "reggeli",
    name: "",
    calories: "",
    protein: "",
    carbs: "",
    fat: "",
    description: ""
  });
  const [savingMeal, setSavingMeal] = useState(false);
  const [editingMealId, setEditingMealId] = useState(null);
  const [mealSearch, setMealSearch] = useState("");
  const [mealTypeFilter, setMealTypeFilter] = useState("all");
  const [mealDateFrom, setMealDateFrom] = useState("");
  const [mealDateTo, setMealDateTo] = useState("");
  const [aiDietPlan, setAiDietPlan] = useState(null);
  const [aiDietLoading, setAiDietLoading] = useState(false);
  const [aiDietApplying, setAiDietApplying] = useState(false);
  const [selectedWeekDay, setSelectedWeekDay] = useState(0);
  const [dietGoal, setDietGoal] = useState("teljesítmény");
  const [dietAllergies, setDietAllergies] = useState([]);
  const [dietType, setDietType] = useState("kiegyensúlyozott");
  const [dietCalories, setDietCalories] = useState(2600);

  useEffect(() => {
    Promise.all([
      api().get("/meals"),
      api().get("/metrics?days=1")
    ]).then(([mRes, metRes]) => {
      setMeals(mRes.data);
      setMetrics(metRes.data[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    if (metrics?.calories_target) {
      setDietCalories(metrics.calories_target);
    }
  }, [metrics]);

  const updateMealField = (field, value) => {
    setMealForm(prev => ({ ...prev, [field]: value }));
  };

  const resetMealForm = () => {
    setMealForm({
      meal_type: "reggeli",
      name: "",
      calories: "",
      protein: "",
      carbs: "",
      fat: "",
      description: ""
    });
    setEditingMealId(null);
  };

  const applyMealDelta = (delta) => {
    setMetrics(prev => {
      const base = prev || {
        calories_target: 2800,
        protein_target: 196,
        carbs_target: 280,
        fat_target: 78
      };
      const safe = (value) => Math.max(0, value || 0);
      return {
        ...base,
        calories_consumed: safe((base.calories_consumed || 0) + (delta.calories || 0)),
        protein: safe((base.protein || 0) + (delta.protein || 0)),
        carbs: safe((base.carbs || 0) + (delta.carbs || 0)),
        fat: safe((base.fat || 0) + (delta.fat || 0))
      };
    });
  };

  const handleMealSubmit = async (e) => {
    e.preventDefault();
    if (!mealForm.name.trim()) {
      toast.error("Add meg az étkezés nevét.");
      return;
    }
    setSavingMeal(true);
    try {
      const payload = {
        meal_type: mealForm.meal_type,
        name: mealForm.name.trim(),
        calories: Number(mealForm.calories) || 0,
        protein: Number(mealForm.protein) || 0,
        carbs: Number(mealForm.carbs) || 0,
        fat: Number(mealForm.fat) || 0,
        description: mealForm.description
      };
      if (editingMealId) {
        const previous = meals.find(m => m.id === editingMealId);
        const res = await api().put(`/meals/${editingMealId}`, payload);
        setMeals(prev => prev.map(m => m.id === editingMealId ? res.data : m));
        if (previous) {
          applyMealDelta({
            calories: payload.calories - (previous.calories || 0),
            protein: payload.protein - (previous.protein || 0),
            carbs: payload.carbs - (previous.carbs || 0),
            fat: payload.fat - (previous.fat || 0)
          });
        }
        toast.success("Étkezés frissítve!");
      } else {
        const res = await api().post("/meals", payload);
        setMeals(prev => [res.data, ...prev]);
        applyMealDelta(payload);
        toast.success("Étkezés rögzítve!");
      }
      resetMealForm();
    } catch {
      toast.error("Nem sikerült menteni az étkezést");
    } finally {
      setSavingMeal(false);
    }
  };

  const handleMealEdit = (meal) => {
    setEditingMealId(meal.id);
    setMealForm({
      meal_type: meal.meal_type || "reggeli",
      name: meal.name || "",
      calories: meal.calories || "",
      protein: meal.protein || "",
      carbs: meal.carbs || "",
      fat: meal.fat || "",
      description: meal.description || ""
    });
  };

  const handleMealDelete = async (meal) => {
    try {
      await api().delete(`/meals/${meal.id}`);
      setMeals(prev => prev.filter(m => m.id !== meal.id));
      applyMealDelta({
        calories: -Number(meal.calories || 0),
        protein: -Number(meal.protein || 0),
        carbs: -Number(meal.carbs || 0),
        fat: -Number(meal.fat || 0)
      });
      if (editingMealId === meal.id) resetMealForm();
      toast.success("Étkezés törölve!");
    } catch {
      toast.error("Nem sikerült törölni az étkezést");
    }
  };

  const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

  const downloadCsv = (filename, headers, rows) => {
    const csv = [headers.join(","), ...rows.map(row => headers.map(key => csvEscape(row[key])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleMealsExport = () => {
    const rows = filteredMeals.map(meal => ({
      date: meal.date,
      meal_type: meal.meal_type,
      name: meal.name,
      calories: meal.calories,
      protein: meal.protein,
      carbs: meal.carbs,
      fat: meal.fat,
      description: meal.description || ""
    }));
    downloadCsv("athlevity_meals.csv", ["date", "meal_type", "name", "calories", "protein", "carbs", "fat", "description"], rows);
  };

  const handleAiNutritionGenerate = async () => {
    const baseCal = Number(dietCalories) || 2600;
    const fallback = {
      title: `Heti ${dietType} étrend – ${dietGoal}`,
      goal: dietGoal,
      base_calories: baseCal,
      weekly_training_min: 0,
      avg_rpe: 0,
      macros: { protein_g: Math.round(baseCal * 0.3 / 4), carbs_g: Math.round(baseCal * 0.4 / 4), fat_g: Math.round(baseCal * 0.3 / 9) },
      weekly_plan: ["Hétfő","Kedd","Szerda","Csütörtök","Péntek","Szombat","Vasárnap"].map(d => ({
        day: d,
        training: "Betöltés...",
        daily_calories: baseCal,
        macros: { protein_g: Math.round(baseCal * 0.3 / 4), carbs_g: Math.round(baseCal * 0.4 / 4), fat_g: Math.round(baseCal * 0.3 / 9) },
        meals: []
      })),
      hydration: "2.5-3 liter víz",
      tips: ["Betöltés..."]
    };
    setAiDietPlan(fallback);
    setSelectedWeekDay(0);
    setAiDietLoading(true);
    try {
      const res = await api().post("/ai/nutrition-plan", {
        goal: dietGoal,
        diet_type: dietType,
        calories_target: baseCal,
        allergies: dietAllergies
      });
      setAiDietPlan(res.data?.weekly_plan ? res.data : fallback);
      toast.success("Heti AI étrend terv elkészült!");
    } catch {
      setAiDietPlan(fallback);
      toast.error("Az AI étrend jelenleg nem elérhető, alap tervet mutatunk.");
    } finally {
      setAiDietLoading(false);
    }
  };

  const formatTraining = (t) => {
    if (!t) return "Nincs edzés";
    if (typeof t === "string") return t;
    if (typeof t === "object") {
      if (Array.isArray(t.names) && t.names.length) return t.names.join(", ");
      if (t.label) return t.label;
      return `${t.total_min || 0} perc ${t.intensity || ""}`.trim();
    }
    return String(t);
  };

  const handleApplyDayMeals = async () => {
    if (!aiDietPlan?.weekly_plan?.[selectedWeekDay]?.meals?.length) {
      toast.error("Nincs alkalmazható étkezés erre a napra.");
      return;
    }
    setAiDietApplying(true);
    const dayPlan = aiDietPlan.weekly_plan[selectedWeekDay];
    let added = 0;
    try {
      for (const meal of dayPlan.meals) {
        const payload = {
          meal_type: meal.meal_type || "ebéd",
          name: meal.name,
          calories: meal.calories || 0,
          protein: meal.protein_g || 0,
          carbs: meal.carbs_g || 0,
          fat: meal.fat_g || 0,
          description: `AI terv – ${dayPlan.day}`
        };
        await api().post("/meals", payload);
        added++;
      }
      const mRes = await api().get("/meals");
      setMeals(mRes.data);
      toast.success(`${added} étkezés hozzáadva (${dayPlan.day})!`);
    } catch {
      toast.error("Hiba az étkezések mentésekor.");
    } finally {
      setAiDietApplying(false);
    }
  };

  const generateDiet = () => {
    setGenerating(true);
    api().post("/ai/recommendation", { context: "nutrition" })
      .then(r => {
        toast.success("Étrend javaslat generálva!");
        toast.info(r.data.recommendation?.slice(0, 150) + "...");
      })
      .catch(() => toast.error("Hiba az étrend generálásánál"))
      .finally(() => setGenerating(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const m = metrics || {};
  const consumed = m.calories_consumed || 2340;
  const target = m.calories_target || 2800;
  const remaining = Math.max(0, target - consumed);
  const protein = m.protein || 165;
  const carbs = m.carbs || 248;
  const fat = m.fat || 68;

  const macroData = [
    { name: "Fehérje", value: protein, color: "#00f260" },
    { name: "Szénhidrát", value: carbs, color: "#ffc107" },
    { name: "Zsír", value: fat, color: "#ff5252" }
  ];

  const totalMacro = protein + carbs + fat;

  const normalizedMealSearch = mealSearch.trim().toLowerCase();
  const filteredMeals = meals.filter((meal) => {
    const matchesSearch = !normalizedMealSearch || [meal.name, meal.description]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedMealSearch);
    const matchesType = mealTypeFilter === "all" || meal.meal_type === mealTypeFilter;
    const matchesFrom = !mealDateFrom || meal.date >= mealDateFrom;
    const matchesTo = !mealDateTo || meal.date <= mealDateTo;
    return matchesSearch && matchesType && matchesFrom && matchesTo;
  });

  return (
    <div className="space-y-6 md:pt-20" data-testid="nutrition-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Táplálkozás</h1>
        <Apple className="text-[#00f260]" size={24} />
      </div>

      {/* Calorie Hero */}
      <GlassCard glow testId="calorie-hero-card" className="text-center py-6 fade-in-up">
        <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">Hátralévő kalória</p>
        <p className="font-heading text-5xl font-bold text-white neon-text mb-1">{remaining}</p>
        <p className="text-sm text-gray-400">{consumed} / {target} kcal elfogyasztva</p>
      </GlassCard>

      {/* Macro Donut + Progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard testId="macro-donut-card" className="fade-in-up fade-in-up-1">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Makró elosztás</p>
          <div className="flex items-center gap-4">
            <div className="w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={macroData} innerRadius={35} outerRadius={55} paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {macroData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3 flex-1">
              {macroData.map((macro) => (
                <div key={macro.name} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: macro.color }} />
                  <span className="text-xs text-gray-400 flex-1">{macro.name}</span>
                  <span className="text-xs text-white font-medium">{macro.value}g</span>
                  <span className="text-[10px] text-gray-500">{Math.round((macro.value / totalMacro) * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* Micronutrient Checklist */}
        <GlassCard testId="micro-checklist-card" className="fade-in-up fade-in-up-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Mikrotápanyag checklist</p>
          <div className="grid grid-cols-2 gap-2">
            {MICRO_CHECKLIST.map((item) => (
              <div key={item.name} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  item.status ? "bg-[#00f260]/20" : "bg-white/5"
                }`}>
                  {item.status && <Check size={12} className="text-[#00f260]" />}
                </div>
                <span className={`text-xs ${item.status ? "text-white" : "text-gray-500"}`}>{item.name}</span>
              </div>
            ))}
          </div>
      </GlassCard>
      </div>

      <GlassCard testId="ai-diet-card" className="fade-in-up fade-in-up-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">AI heti étrend terv</p>
            <p className="text-[10px] text-gray-500">Teljesítmény adatokra épülő 7 napos menü</p>
          </div>
          <button
            data-testid="ai-diet-generate-btn"
            onClick={handleAiNutritionGenerate}
            disabled={aiDietLoading}
            className="neon-btn text-xs px-5 py-2 flex items-center gap-2"
          >
            {aiDietLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            AI terv
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Cél</Label>
            <select
              data-testid="ai-diet-goal-select"
              value={dietGoal}
              onChange={(e) => setDietGoal(e.target.value)}
              className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
            >
              <option value="teljesítmény">Teljesítmény</option>
              <option value="testsúly">Testsúly kontroll</option>
              <option value="regeneráció">Regeneráció</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Típus</Label>
            <select
              data-testid="ai-diet-type-select"
              value={dietType}
              onChange={(e) => setDietType(e.target.value)}
              className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
            >
              <option value="kiegyensúlyozott">Kiegyensúlyozott</option>
              <option value="magas fehérje">Magas fehérje</option>
              <option value="vegetáriánus">Vegetáriánus</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Alap kalória cél</Label>
            <Input
              data-testid="ai-diet-calories-input"
              type="number"
              min="1800"
              step="50"
              value={dietCalories}
              onChange={(e) => setDietCalories(e.target.value)}
              className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>
        </div>

        {/* Allergy filters */}
        <div className="mt-3" data-testid="ai-diet-allergies">
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1"><ShieldAlert size={12} /> Allergiák / diétás megkötések</p>
          <div className="flex flex-wrap gap-2">
            {["gluténmentes", "laktózmentes", "vegán", "vegetáriánus", "tojásmentes", "diómentes", "szójamentes"].map(a => (
              <button
                key={a}
                data-testid={`allergy-${a}`}
                onClick={() => setDietAllergies(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a])}
                className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all border ${
                  dietAllergies.includes(a)
                    ? "bg-red-500/20 text-red-400 border-red-500/40"
                    : "bg-white/5 text-gray-400 border-white/10 hover:border-white/20"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {aiDietPlan ? (
          <div className="mt-4 space-y-4" data-testid="ai-diet-result">
            {/* Header with stats */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm text-white font-medium">{aiDietPlan.title || "Heti étrend terv"}</p>
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                {aiDietPlan.weekly_training_min > 0 && (
                  <span className="flex items-center gap-1"><Dumbbell size={10} /> {aiDietPlan.weekly_training_min} perc/hét</span>
                )}
                {aiDietPlan.avg_rpe > 0 && (
                  <span className="flex items-center gap-1"><Flame size={10} /> RPE {aiDietPlan.avg_rpe}</span>
                )}
              </div>
            </div>

            {/* Base macros overview */}
            <div className="grid grid-cols-3 gap-3 text-xs text-gray-300">
              <div data-testid="ai-diet-protein">Fehérje: {aiDietPlan.macros?.protein_g ?? "-"}g</div>
              <div data-testid="ai-diet-carbs">Szénhidrát: {aiDietPlan.macros?.carbs_g ?? "-"}g</div>
              <div data-testid="ai-diet-fat">Zsír: {aiDietPlan.macros?.fat_g ?? "-"}g</div>
            </div>

            {/* Day selector tabs */}
            {aiDietPlan.weekly_plan?.length > 0 && (
              <>
                <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="ai-diet-day-tabs">
                  {aiDietPlan.weekly_plan.map((dp, idx) => (
                    <button
                      key={dp.day}
                      data-testid={`ai-diet-day-${idx}`}
                      onClick={() => setSelectedWeekDay(idx)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        selectedWeekDay === idx
                          ? "bg-[#00f260]/20 text-[#00f260] border border-[#00f260]/40"
                          : "bg-white/5 text-gray-400 border border-white/5 hover:border-white/20"
                      }`}
                    >
                      {dp.day?.substring(0, 3)}
                    </button>
                  ))}
                </div>

                {/* Selected day detail */}
                {(() => {
                  const dp = aiDietPlan.weekly_plan[selectedWeekDay];
                  if (!dp) return null;
                  return (
                    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 space-y-3" data-testid="ai-diet-day-detail">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white font-medium">{dp.day}</p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5">
                            <Dumbbell size={10} /> {formatTraining(dp.training)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#00f260]" data-testid="ai-diet-day-calories">{dp.daily_calories} kcal</p>
                          {dp.daily_calories !== aiDietPlan.base_calories && (
                            <p className="text-[10px] text-gray-500">
                              {dp.daily_calories > aiDietPlan.base_calories ? "+" : ""}{dp.daily_calories - aiDietPlan.base_calories} az alaphoz
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Day macros bar */}
                      {dp.macros && (
                        <div className="grid grid-cols-3 gap-2 text-[10px]">
                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-blue-400 font-medium">{dp.macros.protein_g}g</p>
                            <p className="text-gray-500">Fehérje</p>
                          </div>
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-amber-400 font-medium">{dp.macros.carbs_g}g</p>
                            <p className="text-gray-500">Szénhidrát</p>
                          </div>
                          <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-rose-400 font-medium">{dp.macros.fat_g}g</p>
                            <p className="text-gray-500">Zsír</p>
                          </div>
                        </div>
                      )}

                      {/* Meals list */}
                      {dp.meals?.length > 0 ? (
                        <div className="space-y-2">
                          {dp.meals.map((meal, idx) => {
                            const MIcon = MEAL_ICONS[meal.meal_type] || Coffee;
                            return (
                              <div key={`ai-weekly-meal-${idx}`} className="flex items-start gap-3 p-2.5 rounded-xl bg-white/5" data-testid={`ai-weekly-meal-${selectedWeekDay}-${idx}`}>
                                <div className="p-1.5 rounded-lg bg-[#00f260]/10 text-[#00f260] mt-0.5">
                                  <MIcon size={12} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-white font-medium truncate">{meal.name}</p>
                                  <p className="text-[10px] text-gray-500 mt-0.5">
                                    {meal.calories} kcal &middot; F {meal.protein_g}g &middot; Sz {meal.carbs_g}g &middot; Zs {meal.fat_g}g
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : aiDietLoading ? (
                        <div className="space-y-2">
                          {[1,2,3,4].map(i => (
                            <div key={`skel-${i}`} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 animate-pulse">
                              <div className="w-8 h-8 rounded-lg bg-white/10" />
                              <div className="flex-1 space-y-1.5">
                                <div className="h-3 bg-white/10 rounded w-2/3" />
                                <div className="h-2 bg-white/5 rounded w-1/2" />
                              </div>
                            </div>
                          ))}
                          <p className="text-[10px] text-gray-500 text-center flex items-center justify-center gap-1.5">
                            <Loader2 size={10} className="animate-spin" /> Étkezések generálása az edzésterved alapján...
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">Nem érhető el étkezés ehhez a naphoz.</p>
                      )}

                      {/* Apply day's meals button */}
                      <button
                        data-testid="ai-diet-apply-day-btn"
                        onClick={handleApplyDayMeals}
                        disabled={aiDietApplying || aiDietLoading || !dp.meals?.length}
                        className="neon-btn w-full text-xs flex items-center justify-center gap-2"
                      >
                        {aiDietApplying ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
                        {aiDietApplying ? "Mentés folyamatban..." : `${dp.day} étkezéseinek hozzáadása`}
                      </button>
                    </div>
                  );
                })()}
              </>
            )}

            <p className="text-xs text-gray-400" data-testid="ai-diet-hydration">Hidratálás: {aiDietPlan.hydration || "-"}</p>
            {aiDietPlan.tips && (
              <ul className="text-xs text-gray-300 space-y-1" data-testid="ai-diet-tips">
                {aiDietPlan.tips.map((tip, idx) => (
                  <li key={`ai-tip-${idx}`}>&#8226; {tip}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-500 mt-4" data-testid="ai-diet-empty">Még nincs generált étrend terv</p>
        )}
      </GlassCard>

      {/* Meal Logger */}
      <GlassCard testId="meal-log-form-card" className="fade-in-up fade-in-up-2">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">{editingMealId ? "Étkezés szerkesztése" : "Étel hozzáadás"}</p>
          <span className="text-[10px] text-gray-500">Mai nap</span>
        </div>
        <form onSubmit={handleMealSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Típus</Label>
              <select
                data-testid="meal-type-select"
                value={mealForm.meal_type}
                onChange={(e) => updateMealField("meal_type", e.target.value)}
                className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
              >
                <option value="reggeli">Reggeli</option>
                <option value="ebéd">Ebéd</option>
                <option value="vacsora">Vacsora</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Étel neve</Label>
              <Input
                data-testid="meal-name-input"
                value={mealForm.name}
                onChange={(e) => updateMealField("name", e.target.value)}
                placeholder="Pl. Csirkés quinoa bowl"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Kalória</Label>
              <Input
                data-testid="meal-calories-input"
                type="number"
                min="0"
                step="1"
                value={mealForm.calories}
                onChange={(e) => updateMealField("calories", e.target.value)}
                placeholder="420"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Fehérje (g)</Label>
              <Input
                data-testid="meal-protein-input"
                type="number"
                min="0"
                step="1"
                value={mealForm.protein}
                onChange={(e) => updateMealField("protein", e.target.value)}
                placeholder="35"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Szénhidrát (g)</Label>
              <Input
                data-testid="meal-carbs-input"
                type="number"
                min="0"
                step="1"
                value={mealForm.carbs}
                onChange={(e) => updateMealField("carbs", e.target.value)}
                placeholder="55"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Zsír (g)</Label>
              <Input
                data-testid="meal-fat-input"
                type="number"
                min="0"
                step="1"
                value={mealForm.fat}
                onChange={(e) => updateMealField("fat", e.target.value)}
                placeholder="12"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Leírás</Label>
            <textarea
              data-testid="meal-description-input"
              value={mealForm.description}
              onChange={(e) => updateMealField("description", e.target.value)}
              placeholder="Összetevők, ízek, egyéb megjegyzés"
              className="w-full min-h-[90px] bg-black/20 border border-white/10 text-white placeholder:text-gray-500 rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>

                    <div className="flex flex-col md:flex-row gap-3">
            <button
              data-testid="meal-submit-btn"
              type="submit"
              disabled={savingMeal}
              className="neon-btn w-full text-sm flex items-center justify-center gap-2"
            >
              {savingMeal ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {editingMealId ? "Étkezés frissítése" : "Étkezés rögzítése"}
            </button>
            {editingMealId && (
              <button
                data-testid="meal-cancel-btn"
                type="button"
                onClick={resetMealForm}
                className="w-full md:w-auto px-6 py-3 rounded-full border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all text-sm"
              >
                Mégse
              </button>
            )}
          </div>
        </form>
      </GlassCard>

      <GlassCard testId="meal-filter-card" className="fade-in-up fade-in-up-3">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Szűrők</p>
          <div className="flex items-center gap-3">
            <button
              data-testid="meal-export-btn"
              onClick={handleMealsExport}
              className="text-xs text-[#00f260] hover:text-white transition"
            >
              CSV export
            </button>
            <button
              data-testid="meal-clear-filters"
              onClick={() => {
                setMealSearch("");
                setMealTypeFilter("all");
                setMealDateFrom("");
                setMealDateTo("");
              }}
              className="text-xs text-gray-400 hover:text-white transition"
            >
              Törlés
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-gray-400 text-xs">Keresés</Label>
            <Input
              data-testid="meal-search-input"
              value={mealSearch}
              onChange={(e) => setMealSearch(e.target.value)}
              placeholder="Étel neve, leírás"
              className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Típus</Label>
            <select
              data-testid="meal-type-filter"
              value={mealTypeFilter}
              onChange={(e) => setMealTypeFilter(e.target.value)}
              className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
            >
              <option value="all">Összes</option>
              <option value="reggeli">Reggeli</option>
              <option value="ebéd">Ebéd</option>
              <option value="vacsora">Vacsora</option>
              <option value="snack">Snack</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Dátum -tól</Label>
            <Input
              data-testid="meal-date-from"
              type="date"
              value={mealDateFrom}
              onChange={(e) => setMealDateFrom(e.target.value)}
              className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Dátum -ig</Label>
            <Input
              data-testid="meal-date-to"
              type="date"
              value={mealDateTo}
              onChange={(e) => setMealDateTo(e.target.value)}
              className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-3" data-testid="meal-results-count">
          Találat: {filteredMeals.length}
        </div>
      </GlassCard>

      {/* Meal Preview Cards */}
      <div className="space-y-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up fade-in-up-3">Mai étkezések</p>
        {filteredMeals.length > 0 ? filteredMeals.map((meal, i) => {
          const Icon = MEAL_ICONS[meal.meal_type] || Coffee;
          return (
            <GlassCard key={meal.id} className={`fade-in-up fade-in-up-${i + 3}`} testId={`meal-card-${meal.meal_type}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00f260]/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-[#00f260]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400 uppercase tracking-wider capitalize">{meal.meal_type}</p>
                    <p className="text-xs text-[#00f260] font-medium">{meal.calories} kcal</p>
                  </div>
                  <p className="text-sm text-white font-medium mt-0.5">{meal.name}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">{meal.description}</p>
                  <div className="flex gap-3 mt-2">
                    <span className="text-[10px] text-gray-400">F: {meal.protein}g</span>
                    <span className="text-[10px] text-gray-400">Sz: {meal.carbs}g</span>
                    <span className="text-[10px] text-gray-400">Zs: {meal.fat}g</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      data-testid={`meal-edit-${meal.id}`}
                      onClick={() => handleMealEdit(meal)}
                      className="px-3 py-1 rounded-full border border-white/10 text-gray-300 text-xs hover:text-white hover:border-white/30 transition-all"
                    >
                      Szerkesztés
                    </button>
                    <button
                      data-testid={`meal-delete-${meal.id}`}
                      onClick={() => handleMealDelete(meal)}
                      className="px-3 py-1 rounded-full border border-red-400/30 text-red-300 text-xs hover:text-red-200 hover:border-red-300 transition-all"
                    >
                      Törlés
                    </button>
                  </div>
                </div>
              </div>
            </GlassCard>
          );
        }) : meals.length > 0 ? (
          <GlassCard className="text-center py-8" testId="meal-empty-filtered">
            <p className="text-gray-400 text-sm">Nincs találat a szűrésre</p>
          </GlassCard>
        ) : (
          <GlassCard className="text-center py-8" testId="meal-empty">
            <p className="text-gray-400 text-sm">Még nincs étkezés rögzítve</p>
          </GlassCard>
        )}
      </div>

      {/* Generate button */}
      <button
        data-testid="generate-diet-btn"
        onClick={generateDiet}
        disabled={generating}
        className="neon-btn w-full text-sm flex items-center justify-center gap-2"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        Étrend generálása
      </button>
    </div>
  );
}
